import bambooWaterDamage from "@/assets/bamboo-water-damage.webp";
import bambooColorMismatch from "@/assets/bamboo-color-mismatch.jpg";
import bambooFinalInstallation from "@/assets/bamboo-final-installation.jpg";
import category3Equipment from "@/assets/category3-equipment-running.webp";
import category3Containment from "@/assets/category3-containment-barrier.jpg";
import category3Ready from "@/assets/category3-ready-for-rebuild.jpg";
import sewerBefore from "@/assets/sewer-backup-before.webp";
import sewageDemoWork from "@/assets/sewage-backup-demo-work.jpg";
import sewerAfter from "@/assets/sewer-backup-after.jpg";
import gainesvilleBuckled from "@/assets/gainesville-exterior-buckled-wall.jpg";
import gainesvilleInventory from "@/assets/gainesville-interior-inventory.png";
import gainesvilleDisplacement from "@/assets/gainesville-exterior-displacement.jpg";
import concertoSitePhotos from "@/assets/concerto-site-photos.jpg";
import concertoMoistureMapping from "@/assets/concerto-moisture-mapping.jpg";
import concertoReportCover from "@/assets/concerto-report-cover.jpg";

export interface Project {
  id: string;
  title: string;
  slug: string;
  location_city: string;
  location_state: string;
  date_completed: string;
  category: string;
  cause: string;
  class_category?: string;
  scope_summary: string;
  impact_area_sqft: number;
  response_time_minutes: number;
  duration_days: number;
  insurer?: string;
  testimonial_quote?: string;
  testimonial_name_city?: string;
  photos: Array<{
    type: 'before' | 'during' | 'after';
    url: string;
    alt: string;
    width: number;
    height: number;
  }>;
  kpis?: {
    rooms_affected: number;
    dehus: number;
    air_movers: number;
    peak_rh?: number;
    dry_standard_achieved: boolean;
    moisture_readings_summary?: string;
  };
  cta_override_text?: string;
}

export const projects: Project[] = [
  {
    id: "1",
    title: "Bamboo Flooring Full Replacement — Canyon Lake, TX",
    slug: "bamboo-flooring-water-damage-full-replacement",
    location_city: "Canyon Lake",
    location_state: "TX",
    date_completed: "2024-03-15",
    category: "Flooring",
    cause: "Burst toilet tank line",
    class_category: "IICRC S500 Class 3, Category 1",
    scope_summary: "Cause: burst toilet tank line. Scope: prove non-matching flooring, secure full replacement claim, 1,500 sq ft bamboo install. Duration: 14 days.",
    impact_area_sqft: 1500,
    response_time_minutes: 90,
    duration_days: 14,
    insurer: "Insurance carrier",
    testimonial_quote: "Ben fought for us with insurance and got us a full floor replacement instead of just patching. We're so grateful.",
    testimonial_name_city: "Homeowner, Canyon Lake",
    photos: [
      {
        type: "before",
        url: bambooWaterDamage,
        alt: "Before – Bamboo floor water damage – Canyon Lake",
        width: 1200,
        height: 900
      },
      {
        type: "during",
        url: bambooColorMismatch,
        alt: "During – Color mismatch documentation – Canyon Lake bamboo flooring",
        width: 1200,
        height: 900
      },
      {
        type: "after",
        url: bambooFinalInstallation,
        alt: "After – New bamboo flooring fully installed – Canyon Lake",
        width: 1200,
        height: 900
      }
    ],
    kpis: {
      rooms_affected: 6,
      dehus: 3,
      air_movers: 12,
      dry_standard_achieved: true,
      moisture_readings_summary: "Full replacement approved due to non-matching materials - turned $4K repair into $35K full replacement"
    }
  },
  {
    id: "2",
    title: "Category 3 Toilet Backup Full Restoration",
    slug: "category-3-toilet-backup-restoration",
    location_city: "Local area",
    location_state: "TX",
    date_completed: "2025-10-04",
    category: "Residential",
    cause: "Toilet backup (Category 3 black water)",
    class_category: "IICRC S500 Class 3, Category 3",
    scope_summary: "Cause: toilet backup (Category 3). Scope: proper assessment, containment, antimicrobial treatment, selective demo, HEPA filtration, full restoration. Duration: 6 days.",
    impact_area_sqft: 800,
    response_time_minutes: 90,
    duration_days: 6,
    insurer: "Insurance carrier",
    testimonial_quote: "They handled a terrible situation with professionalism and care. Our home is safe again thanks to proper IICRC protocols.",
    testimonial_name_city: "Property manager referral",
    photos: [
      {
        type: "before",
        url: category3Equipment,
        alt: "Before – Category 3 toilet backup contamination",
        width: 1200,
        height: 900
      },
      {
        type: "during",
        url: category3Containment,
        alt: "During – Containment barrier and HEPA filtration setup",
        width: 1200,
        height: 900
      },
      {
        type: "after",
        url: category3Ready,
        alt: "After – Properly dried and sanitized, ready for rebuild",
        width: 1200,
        height: 900
      }
    ],
    kpis: {
      rooms_affected: 3,
      dehus: 2,
      air_movers: 10,
      peak_rh: 95,
      dry_standard_achieved: true,
      moisture_readings_summary: "Category 3 protocol followed per IICRC S500 - cleared for safe occupancy and reconstruction"
    }
  },
  {
    id: "3",
    title: "Sewer Backup Full-Home Restoration — New Braunfels, TX",
    slug: "sewer-backup-full-home-restoration",
    location_city: "New Braunfels",
    location_state: "TX",
    date_completed: "2025-01-20",
    category: "Residential",
    cause: "Severe toilet backup - Category 3 contamination",
    class_category: "IICRC S500 Class 4, Category 3",
    scope_summary: "6:00am emergency response. Category 3 black water throughout home. ATP readings >1000. Two-foot flood cuts, custom cabinetry matching (JBR's knotty alder), granite preservation, full tile strip/reseal. 3BR, 2BA, hallway, entry, garage, living room.",
    impact_area_sqft: 2800,
    response_time_minutes: 60,
    duration_days: 45,
    insurer: "Insurance carrier",
    testimonial_quote: "They don't just dry out houses—they bring them back to life. We're thrilled with how we were treated throughout the entire process.",
    testimonial_name_city: "Homeowner, New Braunfels",
    photos: [
      {
        type: "before",
        url: sewerBefore,
        alt: "Before – Bathroom demolition and drying equipment during sewer backup restoration – New Braunfels",
        width: 1200,
        height: 900
      },
      {
        type: "during",
        url: sewageDemoWork,
        alt: "During – Two-foot flood cuts and demo work for Category 3 restoration",
        width: 1200,
        height: 900
      },
      {
        type: "after",
        url: sewerAfter,
        alt: "After – Fully restored bathroom with custom tile, jetted tub, and glass shower – New Braunfels",
        width: 1200,
        height: 900
      }
    ],
    kpis: {
      rooms_affected: 8,
      dehus: 4,
      air_movers: 16,
      peak_rh: 98,
      dry_standard_achieved: true,
      moisture_readings_summary: "ATP >1000 pre-treatment, <30 post-remediation. Insurance gap: $38K estimate vs $80K+ actual cost. Client fully satisfied."
    },
    cta_override_text: "Have a sewage backup emergency? Call WTR Restoration now."
  },
  {
    id: "4",
    title: "Structural Failure Inspection — Gainesville, TX Metal Building",
    slug: "gainesville-metal-building-structural-inspection",
    location_city: "Gainesville",
    location_state: "TX",
    date_completed: "2026-02-17",
    category: "Commercial",
    cause: "Pre-engineered metal building structural failure — snow/ice load",
    class_category: "Structural Event — Formal Protocol",
    scope_summary: "Pre-engineered metal building collapse. Exterior wall displacement, buckled panels, compromised steel framing. Formal emergency protocol developed addressing life safety, load path evaluation, stabilization sequencing, and insurance documentation.",
    impact_area_sqft: 5000,
    response_time_minutes: 120,
    duration_days: 3,
    insurer: "Insurance carrier",
    testimonial_quote: "WTR produced a structured inspection protocol that our engineer and insurance carrier both accepted. Professional and thorough.",
    testimonial_name_city: "Facility Manager, Gainesville",
    photos: [
      {
        type: "before",
        url: gainesvilleBuckled,
        alt: "Before – Buckled metal wall panels showing structural displacement – Gainesville TX",
        width: 1200,
        height: 900
      },
      {
        type: "during",
        url: gainesvilleInventory,
        alt: "During – Palletized inventory beneath compromised steel framing",
        width: 1200,
        height: 900
      },
      {
        type: "after",
        url: gainesvilleDisplacement,
        alt: "Documentation – Full exterior wall displacement along corrugated siding",
        width: 1200,
        height: 900
      }
    ],
    kpis: {
      rooms_affected: 1,
      dehus: 0,
      air_movers: 0,
      dry_standard_achieved: false,
      moisture_readings_summary: "Structural event — load path evaluation and stabilization protocol. Engineering review required before restoration."
    },
    cta_override_text: "Commercial structural damage? Get a qualified inspection immediately."
  },
  {
    id: "5",
    title: "Refrigerator Line Flood — 12-Room Water Mitigation, San Antonio TX",
    slug: "refrigerator-line-flood-12-room-mitigation",
    location_city: "San Antonio",
    location_state: "TX",
    date_completed: "2025-04-16",
    category: "Residential",
    cause: "Refrigerator supply line failure",
    class_category: "IICRC S500 Class 2/3, Category 1",
    scope_summary: "Refrigerator supply line failure flooded 12 rooms. 41 air movers, 5 LGR dehumidifiers, 3 HEPA scrubbers deployed. 10 monitoring visits over 10 days. Full Albi mitigation report submitted to insurance carrier.",
    impact_area_sqft: 1765,
    response_time_minutes: 120,
    duration_days: 10,
    insurer: "State Farm",
    photos: [
      {
        type: "before",
        url: concertoSitePhotos,
        alt: "Before – Standing water and thermal imaging documentation – San Antonio",
        width: 1200,
        height: 900
      },
      {
        type: "during",
        url: concertoMoistureMapping,
        alt: "During – FLIR thermal imaging moisture mapping across affected rooms",
        width: 1200,
        height: 900
      },
      {
        type: "after",
        url: concertoReportCover,
        alt: "After – Complete mitigation report submitted to insurance carrier",
        width: 1200,
        height: 900
      }
    ],
    kpis: {
      rooms_affected: 12,
      dehus: 5,
      air_movers: 41,
      peak_rh: 80,
      dry_standard_achieved: true,
      moisture_readings_summary: "7 moisture points tracked from 19-40% down to 9% dry standard. Full Albi report with daily readings available for download."
    },
    cta_override_text: "Want this level of documentation for your claim? Call WTR now."
  }
];
